/**
 * Performance Monitoring Utilities
 * ✅ PERFORMANCE: Monitor and log performance metrics
 */
class PerformanceMonitor {
    constructor() {
        Object.defineProperty(this, "startTime", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "metrics", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        this.startTime = performance.now();
        this.initializeMonitoring();
    }
    initializeMonitoring() {
        if (process.env.NODE_ENV === 'development') {
            // Monitor page load
            window.addEventListener('load', () => {
                this.measureLoadTime();
                this.measureMemoryUsage();
                this.logMetrics();
            });
            // Monitor bundle sizes in development
            this.logBundleInfo();
        }
    }
    measureLoadTime() {
        const loadTime = performance.now() - this.startTime;
        this.metrics.loadTime = loadTime;
        if (loadTime > 2000) {
            console.warn(`⚠️ Slow load time: ${loadTime.toFixed(2)}ms`);
        }
    }
    measureMemoryUsage() {
        if ('memory' in performance) {
            const memory = performance.memory;
            this.metrics.memoryUsage = memory.usedJSHeapSize;
            const memoryMB = memory.usedJSHeapSize / 1024 / 1024;
            if (memoryMB > 50) {
                console.warn(`⚠️ High memory usage: ${memoryMB.toFixed(2)}MB`);
            }
        }
    }
    logBundleInfo() {
        // Log bundle optimization tips
        console.log('📦 Bundle Optimization Tips:');
        console.log('  - Use code splitting for large dependencies');
        console.log('  - Lazy load routes and components');
        console.log('  - Optimize images (use WebP, compress)');
        console.log('  - Remove unused dependencies');
        console.log('  - Use tree shaking');
    }
    logMetrics() {
        console.group('📊 Performance Metrics');
        if (this.metrics.loadTime) {
            const loadTimeColor = this.metrics.loadTime > 2000 ? '🔴' :
                this.metrics.loadTime > 1000 ? '🟡' : '🟢';
            console.log(`${loadTimeColor} Load Time: ${this.metrics.loadTime.toFixed(2)}ms`);
        }
        if (this.metrics.memoryUsage) {
            const memoryMB = this.metrics.memoryUsage / 1024 / 1024;
            const memoryColor = memoryMB > 50 ? '🔴' : memoryMB > 25 ? '🟡' : '🟢';
            console.log(`${memoryColor} Memory Usage: ${memoryMB.toFixed(2)}MB`);
        }
        console.groupEnd();
    }
    // Method to mark render completion
    markRenderComplete() {
        const renderTime = performance.now() - this.startTime;
        this.metrics.renderTime = renderTime;
        if (process.env.NODE_ENV === 'development') {
            const renderColor = renderTime > 1000 ? '🔴' : renderTime > 500 ? '🟡' : '🟢';
            console.log(`${renderColor} Render Time: ${renderTime.toFixed(2)}ms`);
        }
    }
    // Method to measure specific operations
    measureOperation(name, operation) {
        const start = performance.now();
        const result = operation();
        const duration = performance.now() - start;
        if (process.env.NODE_ENV === 'development' && duration > 10) {
            console.log(`⏱️ ${name}: ${duration.toFixed(2)}ms`);
        }
        return result;
    }
    // Check if Core Web Vitals are good
    checkWebVitals() {
        if ('PerformanceObserver' in window) {
            const observer = new PerformanceObserver((list) => {
                list.getEntries().forEach((entry) => {
                    if (entry.entryType === 'paint') {
                        console.log(`🎨 ${entry.name}: ${entry.startTime.toFixed(2)}ms`);
                    }
                });
            });
            observer.observe({ entryTypes: ['paint'] });
        }
    }
}
// Global performance monitor instance
export const performanceMonitor = new PerformanceMonitor();
// Export utility functions
export function measureAsync(name, asyncOperation) {
    const start = performance.now();
    return asyncOperation().then((result) => {
        const duration = performance.now() - start;
        if (process.env.NODE_ENV === 'development' && duration > 100) {
            console.log(`⏱️ Async ${name}: ${duration.toFixed(2)}ms`);
        }
        return result;
    });
}
export function debounce(func, wait) {
    let timeout = null;
    return (...args) => {
        if (timeout) {
            clearTimeout(timeout);
        }
        timeout = setTimeout(() => {
            func(...args);
        }, wait);
    };
}
export function throttle(func, limit) {
    let inThrottle = false;
    return (...args) => {
        if (!inThrottle) {
            func(...args);
            inThrottle = true;
            setTimeout(() => inThrottle = false, limit);
        }
    };
}
